/**
 * Результаты одной сцены на `/generate`: хук `useSceneHistory` (счётчик у строки сцены) и боковая
 * панель `SceneResultsSheet` — все готовые картинки с этой сценой, фишки по моделям, в новой
 * вкладке открывается карточка результата. Хук и панель берут ту же ленту `/api/fittings`
 * под одним ключом, поэтому на сцену уходит один запрос; пока есть незавершённые — опрос раз в 3 с.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { SideSheet } from "@/components/trends/SideSheet";
import { Chip, Empty, ErrorLine } from "@/components/trends/ui";
import type { SceneRef } from "@/lib/dressSimilar";
import { fittingKeys, loadFittingOptions, loadFittings, type FeedFilter } from "@/lib/fitting";
import { thumbUrl } from "@/lib/media";
import { counterText, modelCounts, sceneHistory } from "@/lib/sceneHistory";

const sceneFilter = (scene: SceneRef): FeedFilter => (scene.kind === "ref" ? { refs: [scene.id] } : { pins: [scene.id] });

/** Лента генераций сцены и свёртка в историю; `query` — для «грузится / ошибка». */
export function useSceneHistory(scene: SceneRef) {
  const filter = sceneFilter(scene);
  const query = useQuery({
    queryKey: fittingKeys.feed(filter),
    queryFn: () => loadFittings(filter),
    refetchInterval: (q) => ((q.state.data?.pending ?? 0) > 0 ? 3_000 : false),
  });
  return { query, history: query.data ? sceneHistory(query.data.gens) : null };
}

export function SceneResultsSheet({ open, onClose, scene }: { open: boolean; onClose: () => void; scene: SceneRef }) {
  const { query, history } = useSceneHistory(scene);
  const options = useQuery({ queryKey: fittingKeys.options, queryFn: loadFittingOptions, enabled: open });
  const [model, setModel] = useState<string | null>(null);

  const done = history?.done ?? [];
  const shown = model === null ? done : done.filter((d) => d.model === model);
  const labelOf = (code: string) => options.data?.models.find((m) => m.code === code)?.label ?? code;

  return (
    <SideSheet
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          {scene.thumb ? (
            <img src={thumbUrl(scene.thumb)} alt="" className="h-12 w-8 flex-none rounded-[4px] object-cover" />
          ) : null}
          <div className="min-w-0">
            <div className="truncate">
              {scene.kind === "ref" ? "Генерации по референсу" : "Генерации по пину"} #{scene.id}
            </div>
            <div className="text-[11.5px] font-normal text-fg-faint">{done.length} фото</div>
          </div>
        </div>
      }
    >
      <div className="grid gap-3 p-4">
        <ErrorLine error={query.error} />
        {history?.truncated ? (
          <div className="text-[12px] text-fg-faint">показаны последние {done.length} фото</div>
        ) : null}
        {done.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            <Chip on={model === null} onClick={() => setModel(null)}>
              все · {done.length}
            </Chip>
            {modelCounts(done).map(([code, n]) => (
              <Chip key={code} on={model === code} onClick={() => setModel(code)}>
                {labelOf(code)} · {n}
              </Chip>
            ))}
          </div>
        ) : null}
        {query.isSuccess && done.length === 0 && !history?.running && !history?.failed ? (
          <Empty>Генераций с этой сценой ещё не было</Empty>
        ) : null}
        {shown.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {shown.map((d) => (
              <a key={d.variant_id} href={`/results/${d.variant_id}`} target="_blank" rel="noopener">
                <img
                  src={thumbUrl(d.asset_path)}
                  alt=""
                  loading="lazy"
                  className="aspect-[9/16] w-full rounded-[4px] object-cover"
                />
              </a>
            ))}
          </div>
        ) : null}
        {history && (history.running > 0 || history.failed > 0) ? (
          <div className="flex gap-3 text-[12px] text-fg-muted">
            {history.running > 0 ? <span>в работе {history.running}</span> : null}
            {history.failed > 0 ? <span className="text-err">упало {history.failed}</span> : null}
          </div>
        ) : null}
      </div>
    </SideSheet>
  );
}

/** Кнопка-счётчик у строки сцены: текст из `useSceneHistory`, клик открывает панель результатов. */
export function SceneCounter({ scene }: { scene: SceneRef }) {
  const { query, history } = useSceneHistory(scene);
  const [open, setOpen] = useState(false);
  const text = history ? counterText(history) : query.isError ? null : "…";
  return (
    <>
      {text !== null ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-none text-[11.5px] text-accent-text hover:underline"
        >
          {text}
        </button>
      ) : null}
      <SceneResultsSheet open={open} onClose={() => setOpen(false)} scene={scene} />
    </>
  );
}
